import { Link, useNavigate } from '@tanstack/react-router';
import { FileText, ListChecks, PlayCircle } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  EntityKey,
  ErrorState,
  ProgressBar,
  ResultBar,
  RunStatusBadge,
  SkeletonRows,
  Stat,
} from '../../components/ui';
import { useFormat, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { CreateRunDialog } from '../test-runs/CreateRunDialog';
import { useProjectOverview } from './api';
import { CoverageCard } from './CoverageCard';

export const ProjectOverviewPage = ({ projectCode }: { projectCode: string }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data, isPending, error, refetch } = useProjectOverview(projectCode);
  const [runOpen, setRunOpen] = useState(false);
  const { t } = useT();
  const { relative } = useFormat();

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending || !data) return <SkeletonRows rows={4} className="p-0" />;

  const { project, lastRunStats } = data;

  return (
    <>
      <PageHeader
        title={t('overview.title')}
        description={project.description ?? undefined}
        actions={
          <>
            {can('case:write') ? (
              <Button
                onClick={() =>
                  void navigate({
                    to: '/projects/$projectCode/test-cases/new',
                    params: { projectCode },
                  })
                }
              >
                <FileText className="size-3.5" />
                {t('overview.newCase')}
              </Button>
            ) : null}
            {can('checklist:write') ? (
              <Button
                onClick={() =>
                  void navigate({
                    to: '/projects/$projectCode/checklists',
                    params: { projectCode },
                  })
                }
              >
                <ListChecks className="size-3.5" />
                {t('overview.newChecklist')}
              </Button>
            ) : null}
            {can('run:create') ? (
              <Button variant="primary" onClick={() => setRunOpen(true)}>
                <PlayCircle className="size-4" />
                {t('overview.newRun')}
              </Button>
            ) : null}
          </>
        }
      />

      <CreateRunDialog open={runOpen} onOpenChange={setRunOpen} projectCode={projectCode} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={t('dashboard.statTestCases')} value={project.counts.testCases} />
        <Stat label={t('dashboard.statChecklists')} value={project.counts.checklists} />
        <Stat label={t('dashboard.statTestRuns')} value={project.counts.testRuns} />
        <Stat
          label={t('dashboard.statActiveRuns')}
          value={project.counts.activeTestRuns}
          valueClassName={project.counts.activeTestRuns > 0 ? 'text-accent-fg' : undefined}
        />
      </div>

      {lastRunStats ? (
        <Card>
          <CardHeader
            title={t('overview.latestResult')}
            description={data.recentRuns[0]?.title}
            action={
              data.recentRuns[0] ? (
                <Link
                  to="/projects/$projectCode/test-runs/$runKey"
                  params={{ projectCode, runKey: data.recentRuns[0].runKey }}
                  className="text-[13px] font-medium text-accent-fg hover:underline"
                >
                  {t('overview.openRun')}
                </Link>
              ) : null
            }
          />
          <div className="flex flex-col gap-3 px-4 py-3">
            <div className="flex items-center gap-3">
              <ProgressBar
                value={lastRunStats.progress}
                className="flex-1"
                label={t('runs.runProgress')}
              />
              <span className="w-52 text-right text-[13px] font-medium text-ink">
                {t('overview.progressSummary', {
                  progress: lastRunStats.progress,
                  passRate: lastRunStats.passRate,
                })}
              </span>
            </div>
            <ResultBar
              passed={lastRunStats.passed}
              failed={lastRunStats.failed}
              blocked={lastRunStats.blocked}
              skipped={lastRunStats.skipped}
              total={lastRunStats.total}
            />
            <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
              <div className="flex gap-1.5">
                <dt className="text-ink-muted">{t('runs.statPassed')}</dt>
                <dd className="font-medium text-pass">{lastRunStats.passed}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-ink-muted">{t('runs.statFailed')}</dt>
                <dd className="font-medium text-fail">{lastRunStats.failed}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-ink-muted">{t('runs.statBlocked')}</dt>
                <dd className="font-medium text-block">{lastRunStats.blocked}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-ink-muted">{t('runs.statSkipped')}</dt>
                <dd className="font-medium text-skip">{lastRunStats.skipped}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-ink-muted">{t('runs.statNotRun')}</dt>
                <dd className="font-medium text-notrun">{lastRunStats.not_run}</dd>
              </div>
            </dl>
          </div>
        </Card>
      ) : null}

      {can('case:read') ? <CoverageCard projectCode={projectCode} /> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={t('overview.recentRuns')}
            action={
              <Link
                to="/projects/$projectCode/test-runs"
                params={{ projectCode }}
                className="text-[13px] font-medium text-accent-fg hover:underline"
              >
                {t('overview.viewAll')}
              </Link>
            }
          />
          {data.recentRuns.length === 0 ? (
            <EmptyState title={t('overview.noRuns')} description={t('overview.noRunsHint')} />
          ) : (
            <ul className="divide-y divide-border">
              {data.recentRuns.map((run) => (
                <li key={run.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <Link
                      to="/projects/$projectCode/test-runs/$runKey"
                      params={{ projectCode, runKey: run.runKey }}
                      className="block truncate text-[13px] font-medium text-ink hover:text-accent-fg"
                    >
                      {run.title}
                    </Link>
                    <p className="mt-0.5 text-[12px] text-ink-subtle">
                      <EntityKey>{run.runKey}</EntityKey> ·{' '}
                      {run.environment?.name ?? t('overview.noEnvironment')} · {relative(run.createdAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-[12px] tabular-nums text-ink-muted">
                      {run.stats.executed}/{run.stats.total}
                    </span>
                    <RunStatusBadge status={run.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader
            title={t('overview.recentCases')}
            action={
              <Link
                to="/projects/$projectCode/test-cases"
                params={{ projectCode }}
                className="text-[13px] font-medium text-accent-fg hover:underline"
              >
                {t('overview.viewAll')}
              </Link>
            }
          />
          {data.recentCases.length === 0 ? (
            <EmptyState title={t('overview.noCases')} description={t('overview.noCasesHint')} />
          ) : (
            <ul className="divide-y divide-border">
              {data.recentCases.map((testCase) => (
                <li key={testCase.id} className="px-4 py-2.5">
                  <Link
                    to="/projects/$projectCode/test-cases/$caseKey"
                    params={{ projectCode, caseKey: testCase.caseKey }}
                    className="block truncate text-[13px] font-medium text-ink hover:text-accent-fg"
                  >
                    {testCase.title}
                  </Link>
                  <p className="mt-0.5 text-[12px] text-ink-subtle">
                    <EntityKey>{testCase.caseKey}</EntityKey> ·{' '}
                    {testCase.folderName ?? t('dashboard.noFolder')} · {relative(testCase.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
};
